import React from 'react';
import {
  Navigation,
  MapPin,
  Clock,
  IndianRupee,
  Star,
  Phone,
  MessageCircle,
  User,
  Car
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button/Button';
import { TRIP_STATUS } from '../../store/tripSlice';


export function TripCard({
  trip, 
  showActions = true, 
  onViewDetails, 
  onCompleteTrip, 
  onCancelTrip,
  className = '' 
}) {
  const getStatusBadge = (status) => {
    const statusConfig = {
      [TRIP_STATUS.COMPLETED]: { variant: 'success', label: 'Completed' },
      [TRIP_STATUS.CANCELLED]: { variant: 'danger', label: 'Cancelled' },
      [TRIP_STATUS.ONGOING]: { variant: 'info', label: 'In Progress' },
      [TRIP_STATUS.ASSIGNED]: { variant: 'warning', label: 'Assigned' },
      [TRIP_STATUS.REQUESTED]: { variant: 'secondary', label: 'Requested' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getElapsedTime = (startTime) => {
    if (!startTime) return null;
    const elapsed = Math.floor((new Date() - new Date(startTime)) / 60000);
    return `${elapsed} min ago`;
  };

  return (
    <Card className={`p-6 hover:shadow-md transition-shadow ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Navigation className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Trip #{trip.id.slice(-6)}
            </h3>
            <p className="text-sm text-gray-500">
              {formatDate(trip.requestedAt)} • {formatTime(trip.requestedAt)}
              {trip.startedAt && trip.status === TRIP_STATUS.ONGOING && (
                <span className="ml-2 text-blue-600">
                  • Started {getElapsedTime(trip.startedAt)}
                </span>
              )}
            </p>
          </div>
        </div>
        {getStatusBadge(trip.status)}
      </div>

      {/* Route Information */}
      <div className="space-y-3 mb-4">
        <div className="flex items-start space-x-3">
          <div className="w-3 h-3 rounded-full bg-green-500 mt-1 flex-shrink-0"></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {trip.pickup.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {trip.pickup.address}
            </p>
          </div>
        </div>
        <div className="flex items-start space-x-3">
          <div className="w-3 h-3 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {trip.dropoff.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {trip.dropoff.address}
            </p>
          </div>
        </div>
      </div>

      {/* Trip Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <User className="w-3 h-3 text-gray-400" />
            <p className="text-xs text-gray-500">Passenger</p>
          </div>
          <p className="text-sm font-medium text-gray-900 truncate">
            {trip.passenger.name}
          </p>
        </div>
        
        {trip.driver && (
          <div>
            <div className="flex items-center space-x-1 mb-1">
              <Car className="w-3 h-3 text-gray-400" />
              <p className="text-xs text-gray-500">Driver</p>
            </div>
            <p className="text-sm font-medium text-gray-900 truncate">
              {trip.driver.name}
            </p>
          </div>
        )}
        
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <MapPin className="w-3 h-3 text-gray-400" />
            <p className="text-xs text-gray-500">Distance</p>
          </div>
          <p className="text-sm font-medium text-gray-900">
            {trip.distance} km
          </p>
        </div>
        
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <IndianRupee className="w-3 h-3 text-gray-400" />
            <p className="text-xs text-gray-500">Fare</p>
          </div>
          <p className="text-sm font-medium text-gray-900">
            ${trip.fare.total}
            {trip.fare.surge > 1 && (
              <span className="text-xs text-orange-600 ml-1">
                ({trip.fare.surge}x)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Rating (for completed trips) */}
      {trip.rating?.passenger && (
        <div className="flex items-center space-x-2 mb-4 p-2 bg-yellow-50 rounded-lg">
          <Star className="w-4 h-4 text-yellow-400 fill-current" />
          <span className="text-sm font-medium text-gray-900">
            {trip.rating.passenger}.0
          </span>
          <span className="text-xs text-gray-500">passenger rating</span>
        </div>
      )}

      {/* Trip Type Badge */}
      <div className="flex items-center justify-between mb-4">
        <Badge variant="info" size="sm">
          {trip.type}
        </Badge>
        {trip.estimatedDuration && (
          <div className="flex items-center space-x-1 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            <span>{trip.estimatedDuration} min</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {showActions && (
        <div className="flex space-x-2">
          {/* View Details Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails?.(trip)}
            className="flex-1"
          >
            View Details
          </Button>

          {/* Status-specific actions */}
          {trip.status === TRIP_STATUS.ONGOING && onCompleteTrip && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onCompleteTrip(trip.id)}
              className="flex-1"
            >
              Complete
            </Button>
          )}

          {[TRIP_STATUS.REQUESTED, TRIP_STATUS.ASSIGNED].includes(trip.status) && onCancelTrip && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancelTrip(trip.id)}
              className="flex-1"
            >
              Cancel
            </Button>
          )}

          {/* Communication buttons for active trips */}
          {[TRIP_STATUS.ONGOING, TRIP_STATUS.ASSIGNED].includes(trip.status) && (
            <>
              <Button variant="outline" size="sm" className="px-3">
                <Phone className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="px-3">
                <MessageCircle className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      )}

      {/* Special Notes */}
      {trip.notes && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-600 font-medium mb-1">Special Notes:</p>
          <p className="text-sm text-blue-800">{trip.notes}</p>
        </div>
      )}
    </Card>
  );
}

/**
 * Compact version of TripCard for list views
 */
export function TripCardCompact({ trip, onViewDetails, className = '' }) {
  const getStatusColor = (status) => {
    const colors = {
      [TRIP_STATUS.COMPLETED]: 'text-green-600',
      [TRIP_STATUS.CANCELLED]: 'text-red-600',
      [TRIP_STATUS.ONGOING]: 'text-blue-600',
      [TRIP_STATUS.ASSIGNED]: 'text-yellow-600',
      [TRIP_STATUS.REQUESTED]: 'text-gray-600'
    };
    return colors[status] || 'text-gray-600';
  };

  return (
    <div 
      className={`p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${className}`}
      onClick={() => onViewDetails?.(trip)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              #{trip.id.slice(-6)}
            </h4>
            <span className={`text-xs font-medium ${getStatusColor(trip.status)}`}>
              {trip.status}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {trip.pickup.name} → {trip.dropoff.name}
          </p>
          <p className="text-xs text-gray-500">
            {trip.passenger.name} • ${trip.fare.total}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">
            {new Date(trip.requestedAt).toLocaleDateString()}
          </p>
          {trip.rating?.passenger && (
            <div className="flex items-center space-x-1 mt-1">
              <Star className="w-3 h-3 text-yellow-400 fill-current" />
              <span className="text-xs text-gray-600">{trip.rating.passenger}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
